import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnInit,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FieldType, FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { bracketsRegex, interpolationRegex } from '@shared/utils/regex.util';
import { DndDropEvent, DndModule } from 'ngx-drag-drop';
import { debounceTime, Subject } from 'rxjs';

@Component({
  selector: 'app-formly-dto-input-type',
  imports: [FormlyModule, FormsModule, ReactiveFormsModule, DndModule],
  templateUrl: './formly-dto-input-type.component.html',
  styleUrl: './formly-dto-input-type.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyDtoInputTypeComponent extends FieldType implements OnInit {
  public dtoValue = signal<string>(null);
  public disabled = signal<boolean>(false);
  public inputField = viewChild<ElementRef>('inputField');

  private inputSubject = new Subject<string>();
  private readonly debounceTime = 300;

  private cdr = inject(ChangeDetectorRef);

  constructor() {
    super();

    this.inputSubject.pipe(debounceTime(this.debounceTime), takeUntilDestroyed()).subscribe((value) => {
      this.checkAndSetDtoValue(value);

      this.formControl.setValue(value);
      this.formControl.updateValueAndValidity();

      this.cdr.markForCheck();
    });
  }

  public ngOnInit(): void {
    if ((this.formControl as FormlyFieldConfig)?.defaultValue?.trim().length) {
      this.checkAndSetDtoValue(this.formControl.value);
    }
  }

  public onInput(event: Event): void {
    const target = event.target as HTMLInputElement;

    this.inputSubject.next(target.value);
    this.cdr.markForCheck();
  }

  public addDtoValue(value: string): void {
    if (value && value.trim().length) {
      this.dtoValue.set(value);
      this.disabled.set(true);
    }
  }

  public remove(): void {
    this.dtoValue.set(null);
    this.disabled.set(false);
    this.formControl.setValue(null);
  }

  public onDrop(event: DndDropEvent): void {
    if (event.data) {
      const data = `{{${event.data.trim()}}}`;

      this.checkAndSetDtoValue(data);
      this.formControl.setValue(data);
    }
  }

  private checkAndSetDtoValue(value: string): void {
    const interpolationMatch = value.match(interpolationRegex);

    if (interpolationMatch) {
      for (const match of interpolationMatch) {
        const key = match.replace(bracketsRegex, '');

        this.addDtoValue(key);
      }
    }
  }
}
